/** @typedef {import('@zenbusiness/application-commons-business-entity').Entitlements} Entitlements */
/** @typedef {import('../config/FlowConfigBase').ScreensToSkipByService} ScreensToSkipByService */

/**
 * Skip if the user is not authenticated.
 * @param {object} options - Options object.
 * @param {boolean} options.authenticated - Indicates if the user is authenticated.
 * @returns {boolean} - True if authenticated, false otherwise.
 */
export const AUTH_SKIP = ({ authenticated }) => !!authenticated;

/**
 * Skip if the cart is empty.
 * @param {object} options - Options object.
 * @param {import("../hooks/useCartContents").CartContents} options.cartContents - The contents of the cart.
 * @returns {boolean} - True if the cart is empty, false otherwise.
 */
export const EMPTY_CART_SKIP = ({ cartContents }) => cartContents.items.length === 0;

/**
 * Skip if page entitlements are present on the business
 * @param {object} options
 * @param {Entitlements} options.entitlements - The business entitlements.
 * @param {object} options.page - Page configuration object.
 * @returns {boolean} - True if the page should be skipped, false otherwise.
 */
export const ENTITLED_SKIP = ({ entitlements, page }) => {
  // If the business entity doesn't have entitlements, skip the page
  if (!(entitlements instanceof Map)) {
    return false;
  }
  if (!page?.entitlements || page?.entitlements.length <= 0) {
    return false;
  }
  return page?.entitlements.every((neededEntitlement) => entitlements.has(neededEntitlement));
};

/**
 * Function that never skips.
 * @returns {boolean} - Always returns false.
 */
export const NEVER_SKIP = () => false;

/**
 * Skip if the business does not have a NY jurisdiction.
 * @param {object} options - Options object.
 * @param {import("../App/Flow").BusinessInfo} options.businessInfo - The business information.
 * @returns {boolean} - True if the business is not in NY jurisdiction, false otherwise.
 */
export const NOT_NY_JURISDICTION = ({ businessInfo }) => {
  return businessInfo?.jurisdictionState !== 'NY';
};

/**
 * Skip if the service is included in the plan contents in the cart.
 *
 * @param {object} options - Options object.
 * @param {object} options.cartContents - The contents of the cart.
 * @param {object} options.page - Page configuration object.
 * @returns {boolean} - True if the plan should be skipped, false otherwise.
 */
export const FORMATION_PLAN_SKIP = ({ cartContents, page }) => {
  return page?.services.some((service) => cartContents.hasServiceInFormationPlan(service));
};

/**
 * Skip if the business entity's planType is NOT "pro" or "premium"
 * The planType for the 'starter' plan is empty.
 *
 * @param {object} params - Parameters for the starter plan skip.
 * @param {object} params.businessEntity - The business entity.
 * @returns {boolean} - True if the page should be skipped, false otherwise.
 */
export const STARTER_PLAN_SKIP = ({ businessEntity }) => {
  return !['pro', 'premium'].includes(businessEntity?.planType);
};

/**
 * Skip if a specific service configured in the flow config is present in the cart.
 *
 * @param {object} options - Options object.
 * @param {object} options.cartContents - The contents of the cart.
 * @param {string} options.page - Page configuration object.
 * @param {Array<ScreensToSkipByService>} options.screensToSkip - Page configuration object.
 * @returns {boolean} - True if the service is in the cart, false otherwise.
 */
export const CART_SERVICE_SKIP = ({ cartContents, page, screensToSkip }) => {
  if (!page?.path || !screensToSkip || screensToSkip.length === 0) {
    return false;
  }

  const screenConfig = screensToSkip.find((screen) => screen.pathToValidate === page.path);
  const serviceToSkip = screenConfig?.skipInService;

  if (!serviceToSkip) {
    return false;
  }

  return cartContents.items.some((item) => item.serviceName === serviceToSkip);
};
